import { pdfHeaderContext, pdfHeaderText, pdfHeaderTag } from "./pdfTagName";
import type { PdfFuseSort } from "./PdfExportConfig";
import { sortFuseConsumers } from "./fuseSort";
import { connectionRouteConsumer } from "./connectionRouteConsumer";
import type Cabinet from "../models/Cabinet";
import type Consumer from "../models/Consumer";
import type Fuse from "../models/Fuse";

export type FuseRouteNode = {
  /** Primary terminal-block consumer, displayed before its incoming cable. */
  primaryConsumer?: { id: string; tag: string };
  key: string;
  kind: "switch" | "supply" | "terminal" | "terminalGap" | "wire" | "consumer" | "note";
  label: string;
  name?: string;
  caption?: { tag: string; name: string; location: string };
  /** Per-connection information, retained even when consumer labels are grouped. */
  detail?: string;
  consumerId?: string;
  icon?: string;
  cable?: { consumerId: string; typeId: string; label: string; wireIndex: number };
  number?: string;
  color?: string;
  dimmed?: boolean;
  group?: { id: string; title: string; itemLabel: string };
};
export type FuseReportConsumer = {
  tag: string;
  name: string;
  channel: string;
  terminal: string;
  route: FuseRouteNode[];
};
export type FuseReportCircuit = { tag: string; name: string; consumers: FuseReportConsumer[] };
export type FuseReportSection = {
  id?: string;
  title: string;
  fi: string;
  fiTitle?: string;
  cabinetTitle?: string;
  circuits: FuseReportCircuit[];
};
export type FuseReport = { sections: FuseReportSection[] };

/** Sorts copies by natural tag order without changing the project's collections. */
function byTag<T extends { tag: string }>(items: Iterable<T>): T[] {
  return Array.from(items).sort((a, b) => a.tag.localeCompare(b.tag, "de", { numeric: true }));
}

/** Resolves the actual cable route, stopping corrupt cycles without losing the consumer. */
function consumerRoute(consumer: Consumer, cabinet: Cabinet): FuseRouteNode[] {
  const route: FuseRouteNode[] = [];
  const channel = consumer.getConnectedChannel();
  const fixedSupply =
    consumer.channelType === "suply230V" ||
    consumer.channelType === "suply400V" ||
    channel?.channelType === "suply230V" ||
    channel?.channelType === "suply400V";
  if (channel && !fixedSupply)
    route.push({
      key: `channel:${channel.id}:switch`,
      kind: "switch",
      label: channel.getRelativeTag(cabinet).replace(/^-/, ""),
      group:
        channel.parent && !fixedSupply
          ? {
              id: channel.parent.id,
              title: channel.parent.getRelativeTag(cabinet).replace(/^-/, ""),
              itemLabel: channel.knxChannel?.shortName ?? channel.tag,
            }
          : undefined,
      dimmed: !fixedSupply && ["Dimmer", "TuneableWhite", "Rgb"].includes(channel.channelType),
    });
  else route.push({ key: `actorGap:${consumer.id}`, kind: "supply", label: "" });
  const terminal = consumer.terminal;
  if (terminal)
    route.push({
      key: `terminal:${terminal.id}`,
      kind: "terminal",
      label: terminal.parent?.tag ?? "Klemme",
      number: terminal.tag,
      group: terminal.parent ? { id: terminal.parent.id, title: terminal.parent.tag, itemLabel: "" } : undefined,
    });
  else route.push({ key: `terminalGap:${consumer.id}`, kind: "terminalGap", label: "" });
  const chain: Consumer[] = [];
  const visited = new Set<string>();
  let current: Consumer | undefined = consumer;
  while (current && !visited.has(current.id)) {
    visited.add(current.id);
    chain.unshift(current);
    current = current.getTerminalViaConsumer();
  }
  if (current) route.push({ key: `cycle:${consumer.id}`, kind: "note", label: "Zyklischer Leitungsweg" });
  for (let index = 0; index < chain.length; index++) {
    const item = chain[index];
    // Each cable belongs to its destination. The next consumer selects its through-wire.
    const next = chain[index + 1];
    const wireIndex = next?.terminalViaWireIndex ?? item.fullWireIndex(0);
    const wire = item.cableType?.wireDefinitions[wireIndex];
    const cableType = item.cableType;
    route.push({
      key: `wire:${item.id}:${wireIndex}`,
      kind: "wire",
      primaryConsumer: index === 0 ? { id: item.id, tag: item.tag } : undefined,
      label: wire?.longName ?? "Ader unbekannt",
      color: wire?.htmlColor,
      cable: cableType?.name?.trim()
        ? {
            consumerId: item.id,
            typeId: cableType.id,
            label: cableType.name,
            wireIndex,
          }
        : undefined,
    });
    route.push(connectionRouteConsumer(item));
  }
  return route;
}

/** Collects one circuit for both representations, including indirect terminal connections. */
function collectCircuit(fuse: Fuse, cabinet: Cabinet, sort: PdfFuseSort): FuseReportCircuit {
  const consumers: FuseReportConsumer[] = [];
  for (const consumer of sortFuseConsumers(fuse.consumers, sort)) {
    const via = consumer.getTerminalViaConsumer();
    consumers.push({
      tag: pdfHeaderTag(consumer),
      name: consumer.name + (via ? ` (via ${via.tag})` : ""),
      channel: consumer.getConnectedChannel()?.getRelativeTag(cabinet) ?? "",
      terminal: consumer.terminal?.getRelativeTag(cabinet) ?? "",
      route: consumerRoute(consumer, cabinet),
    });
  }
  // Fuse.fullTag includes the FI context for schema B and respects the configured DIN/simple prefix.
  const tag = pdfHeaderTag(fuse, { ...pdfHeaderContext(), useTags: true, useDinConvention: true });
  return { tag, name: fuse.name, consumers };
}

/** Keeps FI and FI/LS circuits grouped by cabinet using the same data for list and plan. */
export function collectFuseReport(cabinets: Iterable<Cabinet>, sort: PdfFuseSort = "consumer"): FuseReport {
  const sections: FuseReportSection[] = [];
  for (const cabinet of byTag(cabinets)) {
    const cabinetTitle = pdfHeaderText(cabinet);
    for (const fi of byTag(cabinet.elcbs.asArray())) {
      sections.push({
        id: `${cabinet.id}:${fi.id}`,
        title: pdfHeaderText(fi, { ...pdfHeaderContext(), useTags: true }),
        fi: fi.tag,
        fiTitle: pdfHeaderText(fi, { ...pdfHeaderContext(), useTags: true }),
        cabinetTitle,
        circuits: byTag(fi.fuses.asArray()).map((fuse) => collectCircuit(fuse, cabinet, sort)),
      });
    }
    if (cabinet.fi_ls_s.size)
      sections.push({
        id: `${cabinet.id}:FI/LS`,
        title: "FI/LS",
        fi: "FI/LS",
        fiTitle: "FI/LS",
        cabinetTitle,
        circuits: byTag(cabinet.fi_ls_s.asArray()).map((fuse) => collectCircuit(fuse, cabinet, sort)),
      });
  }
  return { sections };
}

export type FuseRouteBranch = { node: FuseRouteNode; children: FuseRouteBranch[] };
/** Merges adjacent identical prefixes without changing the selected consumer order. */
export function buildFuseRouteTree(consumers: FuseReportConsumer[]): FuseRouteBranch[] {
  const roots: FuseRouteBranch[] = [];
  for (const consumer of consumers) {
    let children = roots;
    for (const node of consumer.route) {
      let branch = children[children.length - 1];
      if (!branch || branch.node.key !== node.key) {
        branch = { node, children: [] };
        children.push(branch);
      }
      children = branch.children;
    }
  }
  return roots;
}
